import { MIME_TYPE_IMAGE_WEBP, isVideo } from '@vestico/utils';

import { CropHints } from './annotations';
import { BraSupportAnswer, ComfortAnswer, FitAnswer, LengthAnswer, SizeAnswer, SkinConcernAnswer } from './answer';
import { DocumentID } from './common';
import { ClientExperiments } from './experiments';
import { FetchBoundaries } from './fetchBoundary';
import { ImageSize, VideoSize, VideoVersion, getImagePath, getVideoPath } from './posts';
import { ProductVariantUID } from './products/product';
import { AdditionalSizeChartOption, SizeChart, SizeChartItem } from './providers';
import { BraSizeSourceID, EyeColorModel, HairToneModel, SkinSensitivityModel, SkinToneModel, SkinTypeModel, SkinUndertoneModel } from './userFields';
import { SupportedMatchInfoHashCategory } from './userMatchInfoHash';
import { Gender } from './users/constants';
import { HeightUnit, UserModel, WeightUnit, userDataCompleted, userDataIsMatchable } from './users/user';
import { ClientDetails } from './visitEvent';

export type ReviewRequestUserInfo = {
  gender: Gender;
  height?: number;
  weight?: number;
  birthyear?: number;
  braSizeBand?: number;
  braSizeCup?: number;
  braSizeSourceId?: BraSizeSourceID;
  tummyShape?: number;
  hipShape?: number;
  shoulderShape?: number;
  heightUnit: HeightUnit;
  weightUnit: WeightUnit;
  // beauty
  skinTone?: SkinToneModel;
  skinType?: SkinTypeModel;
  skinUndertone?: SkinUndertoneModel;
  skinSensitivity?: SkinSensitivityModel;
  eyeColor?: EyeColorModel;
  hairTone?: HairToneModel;
};

export interface MatchRequest {
  product?: string;
  selectedVariantUID?: ProductVariantUID;
  provider: string;
  user?: string;
  userInfo?: ReviewRequestUserInfo;
  clientDetails: ClientDetails;
  isPreview?: boolean;
  isSDK?: boolean;
  experiments?: ClientExperiments;
}

export type SizeRecommendationRequest = MatchRequest;

export interface ReviewRequest extends MatchRequest {
  selectedPost?: string;
  existingPostsToKeep?: string[];
}

export type ProductCollectionUID = string | 'all';

export interface ContentReviewRequest extends ReviewRequest {
  entryID?: string;
  reloadAll?: boolean;
  referenceFetchBoundaries?: FetchBoundaries;
  productCollectionUID?: ProductCollectionUID;
}

export type ImageSource = {
  [size in ImageSize]?: string;
};

export type VideoSource = {
  [size in VideoSize]?: string;
};

export type ServerImageSourceSet = { [mimeType: string]: ImageSize[] };
export type ServerVideoSourceSet = { [mimeType: string]: VideoSize[] };
export type ClientImageSourceSet = { [mimeType: string]: ImageSource };
export type ClientVideoSourceSet = { [mimeType: string]: VideoSource };

export interface BaseMedia {
  mainMimeType: string;
  cropHints?: CropHints;
}

export type ServerImageSource = { path: string; sources: ServerImageSourceSet };
export type ServerVideoSource = { path: string; sources: ServerVideoSourceSet };
export type ServerMediaSource = ServerImageSource | ServerVideoSource;

export interface ServerMedia extends BaseMedia {
  domain?: string;
  signedURLParams?: string;
  imageSource: ServerImageSource;
  videoSource?: ServerVideoSource;
}

export interface ClientMedia extends BaseMedia {
  imageSources: ClientImageSourceSet;
  videoSources?: ClientVideoSourceSet;
  videoPreviews?: ClientVideoSourceSet;
}

export interface ReviewProductInfo {
  documentID: DocumentID;
  variantUID?: string;
  variantName?: string;
  size?: string;
}

export type ReviewResponseType = 'bodydouble' | 'textreview';
export interface BaseReviewResponse {
  postDocumentID: DocumentID;
  products: ReviewProductInfo[];
  timestampCreated: Date;
  type: ReviewResponseType;
  hasMatchInfo: boolean;
  likes: number;
  // USER INPUT
  userName?: string;
  reviewText?: string;
  reviewTextLanguage?: string;
  // FASHION PROPERTIES
  height?: number;
  braSizeBand?: number;
  braSizeCup?: number;
  usualSizeAnswer?: string;
  braSizeAnswer?: SizeAnswer;
  pantySizeAnswer?: SizeAnswer;
  braSupportAnswer?: BraSupportAnswer;
  fitAnswer?: FitAnswer;
  sizeAnswer?: SizeAnswer;
  lengthAnswer?: LengthAnswer;
  comfortAnswer?: ComfortAnswer;
  // BEAUTY PROPERTIES
  skinTone?: SkinToneModel;
  skinUndertone?: SkinUndertoneModel;
  skinType?: SkinTypeModel;
  hairTone?: HairToneModel;
  eyeColor?: EyeColorModel;
  skinSensitivity?: SkinSensitivityModel;
  skinConcernAnswers?: SkinConcernAnswer[];
}

interface MatchedResponseProps {
  matchValue?: number;
  weightMatchValue?: number;
  hideMatchValue?: boolean;
}

interface ServerMediaProps {
  media: ServerMedia;
}

interface ClientMediaProps {
  media: ClientMedia;
}

export type ServerBaseResponse = BaseReviewResponse & ServerMediaProps;
export type ServerMatchedResponse = ServerBaseResponse & MatchedResponseProps;
export type ServerTextReviewResponse = ServerBaseResponse;
export type ServerReviewResponse = ServerMatchedResponse | ServerTextReviewResponse;

export type ClientBaseResponse = BaseReviewResponse & ClientMediaProps;
export type ClientMatchedResponse = ClientBaseResponse & MatchedResponseProps;
export type ClientTextReviewResponse = ClientBaseResponse;
export type ClientReviewResponse = ClientMatchedResponse | ClientTextReviewResponse;

export interface SizeRecommendation {
  /**
   * @deprecated Use variantOptions instead
   */
  variant: string;
  variantOptions: string[];
  variantUID: string;
  confidence: number;
  available: boolean;
  isLarger?: boolean;
  isLowConfidenceResult?: boolean;
}

export interface SizeChartDisplayResult {
  items: SizeChartItem[];
  additionalOptions: AdditionalSizeChartOption[];
  flipped?: boolean;
  unmatched?: boolean;
  description?: string;
}

export interface BaseRequestResult {
  userUID?: string;
}

export interface SizeRecommendationRequestResultProps {
  recommendations: SizeRecommendation[];
  chart?: SizeChartDisplayResult;
  additionalProductAttributes?: string[];
  alternativeHintDisabled?: boolean;
  customDisclaimer?: string;
}

export interface BaseReviewRequestResult extends BaseRequestResult {
  anyResponseHasMatchInfo: boolean;
  anyResponseHasMatch: boolean;
  fetchBoundaries?: FetchBoundaries;

  sizeRecommendationResult?: SizeRecommendationRequestResultProps;

  contentInfoCTADisabled?: boolean;
}

export interface ServerReviewRequestResult extends BaseReviewRequestResult {
  reviews: ServerReviewResponse[];
}

export interface ClientReviewRequestResult extends BaseReviewRequestResult {
  reviews: ClientReviewResponse[];
}

export type SizeRecommendationRequestResult = BaseRequestResult & SizeRecommendationRequestResultProps;

export interface CreateReviewRequestUserInfoOptions {
  category?: SupportedMatchInfoHashCategory;
  requiresCompleted?: boolean;
}

const defaultOptions: CreateReviewRequestUserInfoOptions = {};

export const createReviewRequestUserInfo = (user: UserModel, options?: CreateReviewRequestUserInfoOptions): ReviewRequestUserInfo | undefined => {
  const { requiresCompleted, category } = options ?? defaultOptions;
  if (!userDataIsMatchable(user, category)) {
    return undefined;
  }

  if (requiresCompleted && !userDataCompleted(user, category, false, false)) {
    return undefined;
  }

  const {
    gender,
    birthyear,
    height,
    weight,
    braSizeBand,
    braSizeCup,
    hipShape,
    shoulderShape,
    tummyShape,
    braSizeSourceId,
    heightUnit,
    weightUnit,
    skinTone,
    skinType,
    skinSensitivity,
    skinUndertone,
    eyeColor,
    hairTone,
  } = user;

  const userInfo: ReviewRequestUserInfo = {
    gender: gender ?? Gender.unset,
    birthyear,
    height: height ?? 0,
    weight,
    braSizeBand,
    braSizeCup,
    hipShape,
    shoulderShape,
    tummyShape,
    braSizeSourceId,
    heightUnit,
    weightUnit,
    skinTone,
    skinType,
    hairTone,
    skinUndertone,
    eyeColor,
    skinSensitivity,
  };
  return userInfo;
};

const createClientMediaSources = (
  serverSource: ServerMediaSource,
  domain: string | undefined,
  signedURLParams: string | undefined,
  videoVersion?: VideoVersion
): ClientImageSourceSet | ClientVideoSourceSet => {
  const { path, sources } = serverSource;

  const clientSourceSet: ClientImageSourceSet | ClientVideoSourceSet = {};

  for (const mimeType of Object.keys(sources)) {
    const sizes = sources[mimeType];
    const isVideoFiles = isVideo(mimeType);
    const source: ImageSource | VideoSource = {};
    for (const size of sizes) {
      const versionPath = isVideoFiles
        ? // we always path 'full' version for Video as the preview suffix is already part of the path
          getVideoPath(path, videoVersion ?? 'full', size as VideoSize, mimeType)
        : getImagePath(path, size as ImageSize, mimeType === MIME_TYPE_IMAGE_WEBP ? '.webp' : undefined);
      const separator = versionPath.includes('?') ? '&' : '?';

      source[size] = `https://${domain}/${versionPath}${separator}${signedURLParams}`;
    }

    clientSourceSet[mimeType] = source as ImageSource | VideoSource;
  }

  return clientSourceSet;
};

export const createClientResponse = (serverResponse: ServerReviewRequestResult) => {
  const { userUID, anyResponseHasMatch, anyResponseHasMatchInfo, fetchBoundaries, reviews: serverReviews, sizeRecommendationResult } = serverResponse;

  const clientReviews = serverReviews.map((serverResponse) => {
    const { media: serverMedia } = serverResponse;
    const { imageSource, videoSource, mainMimeType, cropHints, domain, signedURLParams } = serverMedia;

    const clientMedia: ClientMedia = {
      mainMimeType,
      cropHints,
      imageSources: createClientMediaSources(imageSource, domain, signedURLParams),
      videoSources: videoSource ? createClientMediaSources(videoSource, domain, signedURLParams) : undefined,
      videoPreviews: videoSource ? createClientMediaSources(videoSource, domain, signedURLParams, 'preview') : undefined,
    };

    const clientResponse: ClientReviewResponse = { ...serverResponse } as unknown as ClientReviewResponse;
    clientResponse.media = clientMedia;
    return clientResponse;
  });

  const clientResponse: ClientReviewRequestResult = {
    userUID,
    anyResponseHasMatch,
    anyResponseHasMatchInfo,
    fetchBoundaries,
    reviews: clientReviews,
    sizeRecommendationResult,
  };

  return clientResponse;
};

export const createSizeChartDisplayResult = (sizeChart: SizeChart) => {
  // Transform to minimal result w/o any other size chart properties
  const { additionalOptions, itemsConfig, unmatched } = sizeChart;
  const { items, displayInStore, description } = itemsConfig;

  // Filter out any items/options that are not displayed
  const sizeChartDisplay: SizeChartDisplayResult = {
    items: displayInStore ? items : [],
    additionalOptions: additionalOptions.filter((o) => o.displayInStore),
    flipped: true,
    unmatched,
    description,
  };

  const isEmptyDisplaySizeChart = sizeChartDisplay.items.length === 0 && sizeChartDisplay.additionalOptions.length === 0;
  if (isEmptyDisplaySizeChart) {
    return undefined;
  }

  return sizeChartDisplay;
};

export const getTitleForVariantOptions = (variantOptions: string[]) => {
  return variantOptions.join(' - ');
};
